import axios, { AxiosError, AxiosResponse } from 'axios'
import qs from 'qs'

axios.defaults.withCredentials = true
axios.defaults.paramsSerializer = {
    serialize: params => qs.stringify(params, { arrayFormat: 'repeat' })
}
const getHeaders = ((token: string) => {
    return {
        headers: {
            Authorization: `Bearer ${token}`,
            'Access-Control-Allow-Credentials': true
        }
    }
})

function promise<Type>(request: Promise<AxiosResponse<Type>>): Promise<Type> {
    return new Promise((resolve, reject) => {
        request
            .then(response => resolve(response.data))
            .catch(error => reject(error))
    })
}

type FailureHandler<FailureType, FailureResponse> = (error: AxiosError<FailureResponse>) => FailureType | undefined
export type Result<SuccessType, FailureType> = { success: true; value: SuccessType } | { success: false; error: FailureType }
function promiseResult<SuccessType, FailureType, FailureResponse>(
    request: Promise<AxiosResponse<SuccessType>>,
    failureHandler: FailureHandler<FailureType, FailureResponse>): Promise<Result<SuccessType, FailureType>> {
    return new Promise((resolve, reject) => request
        .then(response => resolve({ success: true, value: response.data }))
        .catch(e => {
            const error = e as AxiosError<FailureResponse>
            if (failureHandler !== undefined) {
                const result = failureHandler(error)
                if (result !== undefined) {
                    resolve({ success: false, error: result })
                    return
                }
            }
            reject(error)
        })
    )
}

export const AxiosService = new (class {
    get<Type>(url: string): Promise<Type> {
        return promise(axios.get(url))
    }

    getAuth<Type>(url: string, userToken: string, params?: { [key: string]: any }): Promise<Type> {
        return promise(axios.get(url, { ...getHeaders(userToken), params: params ?? {} }))
    }

    post<Type>(url: string): Promise<Type> {
        return promise(axios.post(url))
    }

    postAuth<Type>(url: string, userToken: string, params?: { [key: string]: any }, body?: any): Promise<Type> {
        return promise(axios.post(url, body ?? null, { ...getHeaders(userToken), params: params ?? {} }))
    }

    postAuthResult<Type, FailureType, FailureResponse>(url: string, userToken: string, failureHandler: FailureHandler<FailureType, FailureResponse>, params?: { [key: string]: any }, body?: any): Promise<Result<Type, FailureType>> {
        return promiseResult(axios.post(url, body ?? null, { ...getHeaders(userToken), params: params ?? {} }), failureHandler)
    }

    putAuth<Type>(url: string, userToken: string, params?: { [key: string]: any }, body?: any): Promise<Type> {
        return promise(axios.put(url, body ?? null, { ...getHeaders(userToken), params: params ?? {} }))
    }

    putAuthResult<Type, FailureType, FailureResponse>(url: string, userToken: string, failureHandler: FailureHandler<FailureType, FailureResponse>, params?: { [key: string]: any }, body?: any): Promise<Result<Type, FailureType>> {
        return promiseResult(axios.put(url, body ?? null, { ...getHeaders(userToken), params: params ?? {} }), failureHandler)
    }

    deleteAuth<Type>(url: string, userToken: string, params?: { [key: string]: any }): Promise<Type> {
        return promise(axios.delete(url, { ...getHeaders(userToken), params: params ?? {} }))
    }
})()
